import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Organization } from '../types';
import { api } from '../services/api';

interface AuthResult {
  user: User;
  organization: Organization;
  role: string;
}

interface AuthContextType {
  user: User | null;
  organization: Organization | null;
  role: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, subdomain?: string) => Promise<AuthResult>;
  register: (data: {
    email: string;
    password: string;
    fullName: string;
    organizationName?: string;
    subdomain?: string;
    inviteToken: string;
  }) => Promise<AuthResult>;
  logout: () => void;
  updateOrganization: (patch: Partial<Organization>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const isRootDomain = parts.length === 2 || (parts.length === 3 && parts[0] === 'www');
    if (isRootDomain) {
      setIsLoading(false);
      return;
    }
    const token = localStorage.getItem('auth_token');
    if (token) {
      api.getMe()
        .then((data) => {
          setUser(data.user);
          setOrganization(data.organization);
          setRole(data.role);
        })
        .catch(() => {
          api.clearToken();
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string, subdomain?: string): Promise<AuthResult> => {
    const data = await api.login(email, password, subdomain);
    setUser(data.user);
    setOrganization(data.organization);
    setRole(data.role);
    return { user: data.user, organization: data.organization, role: data.role };
  };

  const register = async (data: {
    email: string;
    password: string;
    fullName: string;
    organizationName?: string;
    subdomain?: string;
    inviteToken: string;
  }): Promise<AuthResult> => {
    const result = await api.register(data);
    setUser(result.user);
    setOrganization(result.organization);
    setRole(result.role);
    return { user: result.user, organization: result.organization, role: result.role };
  };

  const logout = () => {
    api.clearToken();
    setUser(null);
    setOrganization(null);
    setRole(null);
  };

  const updateOrganization = (patch: Partial<Organization>) => {
    setOrganization(prev => (prev ? { ...prev, ...patch } : prev));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        role,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        updateOrganization,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
